"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import { ButtonLink } from "@/components/ui/button-link";

interface CtaBannerProps {
  title: string;
  description?: string;
  ctaText: string;
  ctaHref: string;
  variant?: "primary" | "dark" | "gradient";
}

const CtaBanner = ({ title, description, ctaText, ctaHref, variant = "primary" }: CtaBannerProps) => {
  const variantStyles = {
    primary: "bg-secondary text-foreground",
    dark: "bg-foreground text-white",
    gradient: "bg-gradient-to-br from-foreground via-foreground/95 to-foreground/80 text-white",
  };

  const buttonVariant = variant === "primary" ? "default" : "primary-secondary";

  return (
    <section className="py-16 px-6 bg-white">
      <div className="container max-w-4xl mx-auto">
        <BlurFade delay={0.1}>
          <div className={`rounded-[2.5rem] p-10 md:p-14 flex flex-col items-center text-center gap-6 relative overflow-hidden ${variantStyles[variant]}`}>
            {/* Subtle decorative elements for dark/gradient variants */}
            {variant !== "primary" && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-[-30%] right-[-15%] w-[50%] aspect-square border border-white/5 rounded-full" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[40%] aspect-square border border-white/5 rounded-full" />
              </div>
            )}

            <div className="relative z-10 space-y-3">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                {title}
              </h2>
              {description && (
                <p className={`text-base md:text-lg max-w-xl mx-auto ${variant === "primary" ? "text-foreground/60" : "text-white/60"}`}>
                  {description}
                </p>
              )}
            </div>

            <BlurFade delay={0.2} className="relative z-10">
              <ButtonLink
                href={ctaHref}
                variant={buttonVariant as any}
                size="lg"
                className="font-bold shadow-lg"
              >
                {ctaText}
              </ButtonLink>
            </BlurFade>
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default CtaBanner;
